import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Trash2 } from "lucide-react";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useQpmOfferDeleteMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";

export function DeleteOfferDialog({
  offerId,
  disabled,
  triggerLabel,
}: {
  offerId: string;
  disabled?: boolean;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [doDelete, { isLoading, error, isSuccess, reset }] =
    useQpmOfferDeleteMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (!isSuccess) return;
    setOpen(false);
    reset();
    navigate("..");
  }, [isSuccess, navigate, reset]);

  const onClick = () => {
    if (disabled || isLoading) {
      return;
    }

    doDelete({ id: offerId });
  };

  if (!triggerLabel) {
    return (
      <DialogTooltipButton
        tooltip={t("Angebot löschen")}
        dialogTitle={t("Angebot wirklich löschen?")}
        icon={<Trash2 className="h-5 w-5" />}
        buttonProps={{ disabled }}
        content={
          <>
            <RTKQueryErrorAlert error={error} />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{t("Abbrechen")}</Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={onClick}
                disabled={disabled || isLoading}
              >
                {t("Löschen")}
              </Button>
            </DialogFooter>
          </>
        }
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className="w-full justify-start"
        >
          {triggerLabel}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={onClick}
            disabled={disabled || isLoading}
          >
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
